// OrderSummary.tsx
import React, { useEffect, useMemo, useState } from "react";
import DateRangePicker from "react-bootstrap-daterangepicker";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import CollapseHeader from "../../../core/common/collapse-header";
import { all_routes } from "../../../router/backendComponents/admin/all_routes";
import Pagination from "../../../formHelper/pagination";
import apiRequestCall from "../../../apiHelper/globalHelper";
import Table from "../../../formHelper/list";
import Loader from "../../../formHelper/Loader";

const OrderSummary: React.FC = () => {
  const backendRoute = all_routes.backend;

  // 👇 Individual loaders
  const [principalLoading, setPrincipalLoading] = useState(false);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [branchLoading, setBranchLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  // principal table
  const [principalOrder, setPrincipalOrders] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(5);
  const [totalItems, setTotalItems] = useState(0);

  // customer table
  const [customerOrder, setCustomerOrders] = useState<any[]>([]);
  const [currentCustomerPage, setCustomerCurrentPage] = useState(1);
  const [perCustomerPage, setCustomerPerPage] = useState(5);
  const [totalCustomerItems, setCustomerTotalItems] = useState(0);

  // branch chart state
  const [branchSeriesData, setBranchSeriesData] = useState<number[]>([]);
  const [branchCategories, setBranchCategories] = useState<string[]>([]);
  const [branchYAxis, setBranchYAxis] = useState<{
    min: number;
    max: number;
    tickAmount?: number;
  }>({
    min: 0,
    max: 0,
    tickAmount: 5,
  });

  // status pie
  const [statusOrders, setStatusOrders] = useState<number[]>([0, 0, 0, 0]);

  // Reason
  const [reasonsLoading, setReasonsLoading] = useState(false);
  const [reasonsCats, setReasonsCats] = useState<string[]>([]);
  const [reasonsData, setReasonsData] = useState<number[]>([]);

  // helpers
  const toArray = <T,>(v: unknown, mapFn?: (x: any) => T): T[] => {
    const arr = Array.isArray(v)
      ? v
      : v && typeof v === "object"
      ? Object.values(v as Record<string, any>)
      : [];
    return mapFn ? arr.map(mapFn) : (arr as T[]);
  };
  const toNumberArray = (v: unknown) =>
    toArray<number>(v, (x) => Number(x) || 0);
  const toStringArray = (v: unknown) =>
    toArray<string>(v, (x) => String(x ?? ""));

  const reasonsSeries: ApexAxisChartSeries = useMemo(
    () => [{ name: "Orders", data: reasonsData }],
    [reasonsData]
  );

  const reasonsOptions: ApexOptions = useMemo(
    () => ({
      chart: { type: "bar", height: 300 },
      plotOptions: { bar: { horizontal: true, barHeight: "70%" } },
      dataLabels: {
        enabled: true,
        formatter: (val) => (Number(val) || 0).toLocaleString(),
        offsetX: 8,
      },
      tooltip: { y: { formatter: (v) => (v ?? 0).toLocaleString() } },

      xaxis: {
        categories: reasonsCats,
        labels: { formatter: (v) => Number(v).toLocaleString() },
      },
      yaxis: {
        labels: { maxWidth: 220 },
      },

      legend: { show: false },
      colors: ["#FC0027"],
    }),
    [reasonsCats]
  );

  const pieOptions = useMemo<ApexOptions>(
    () => ({
      chart: { width: 400, height: 300, type: "pie" },
      legend: { position: "bottom" },
      labels: ["pending", "partial", "dispatched", "close"],
      responsive: [
        {
          breakpoint: 480,
          options: { chart: { width: 275 }, legend: { position: "bottom" } },
        },
      ],
    }),
    []
  );

  // branch chart (dynamic)
  const branchSeries: ApexAxisChartSeries = useMemo(
    () => [{ name: "Amount", data: branchSeriesData }],
    [branchSeriesData]
  );
  const branchOptions: ApexOptions = useMemo(
    () => ({
      chart: { height: 273, type: "area", zoom: { enabled: false } },
      colors: ["#4A00E5"],
      dataLabels: { enabled: false },
      title: { text: "", align: "left" },
      xaxis: { categories: branchCategories },
      yaxis: {
        min: branchYAxis.min,
        max: branchYAxis.max,
        tickAmount: branchYAxis.tickAmount ?? 5,
      },
      legend: { position: "top", horizontalAlign: "left" },
      tooltip: { y: { formatter: (v) => (v ?? 0).toLocaleString() } },
    }),
    [branchCategories, branchYAxis]
  );

  const initialSettings = {
    endDate: new Date("2020-08-11T12:30:00.000Z"),
    ranges: {
      "Last 30 Days": [
        new Date("2020-07-12T04:57:17.076Z"),
        new Date("2020-08-10T04:57:17.076Z"),
      ],
      "Last 7 Days": [
        new Date("2020-08-04T04:57:17.076Z"),
        new Date("2020-08-10T04:57:17.076Z"),
      ],
      "Last Month": [
        new Date("2020-06-30T18:30:00.000Z"),
        new Date("2020-07-31T18:29:59.999Z"),
      ],
      "This Month": [
        new Date("2020-07-31T18:30:00.000Z"),
        new Date("2020-08-31T18:29:59.999Z"),
      ],
      Today: [
        new Date("2020-08-10T04:57:17.076Z"),
        new Date("2020-08-10T04:57:17.076Z"),
      ],
      Yesterday: [
        new Date("2020-08-09T04:57:17.076Z"),
        new Date("2020-08-09T04:57:17.076Z"),
      ],
    },
    startDate: new Date("2020-08-04T04:57:17.076Z"),
    timePicker: false,
  };

  // tables
  const columns = useMemo(
    () => [
      {
        key: "id",
        label: "Sr. No",
        render: (_: any, i: number) => (currentPage - 1) * perPage + i + 1,
      },
      { key: "principal_name", label: "Principal Name" },
      { key: "total_amount", label: "Total Amount" },
      { key: "orders_count", label: "Order Counts" },
    ],
    [currentPage, perPage]
  );
  const columnCustomer = useMemo(
    () => [
      {
        key: "id",
        label: "Sr. No",
        render: (_: any, i: number) =>
          (currentCustomerPage - 1) * perCustomerPage + i + 1,
      },
      { key: "company_name", label: "Company Name" },
      { key: "customer_name", label: "Customer Name" },
      { key: "total", label: "Total Amount" },
      { key: "orders_count", label: "Order Counts" },
    ],
    [currentCustomerPage, perCustomerPage]
  );

  // API calls with independent loaders
  const fetchOrderPrincipal = async ({ page = 1, perPage = 5 }) => {
    setPrincipalLoading(true);
    try {
      const res = await apiRequestCall(
        "post",
        backendRoute.principalWiseOrder,
        {
          data: {
            from: startDate,
            to: endDate,
            show_all: true,
            page,
            per_page: perPage,
          },
        }
      );
      const payload = res?.data ?? {};
      const paginator = payload?.data ?? {};
      if (payload?.status || res?.code === 200) {
        setPrincipalOrders(paginator?.data || []);
        setCurrentPage(paginator?.current_page || 1);
        setPerPage(paginator?.per_page || 5);
        setTotalItems(paginator?.total || 0);
      } else {
        setPrincipalOrders([]);
        setCurrentPage(1);
        setPerPage(5);
        setTotalItems(0);
      }
    } finally {
      setPrincipalLoading(false);
    }
  };

  const fetchCustomerOrder = async ({ page = 1, perCustomerPage = 5 }) => {
    setCustomerLoading(true);
    try {
      const res = await apiRequestCall("post", backendRoute.companyWiseOrder, {
        data: {
          from: startDate,
          to: endDate,
          show_all: true,
          page,
          per_page: perCustomerPage,
        },
      });
      const payload = res?.data ?? {};
      const paginator = payload?.data ?? {};
      if (payload?.status || res?.code === 200) {
        setCustomerOrders(paginator?.data || []);
        setCustomerCurrentPage(paginator?.current_page || 1);
        setCustomerPerPage(paginator?.per_page || 5);
        setCustomerTotalItems(paginator?.total || 0);
      } else {
        setCustomerOrders([]);
        setCustomerCurrentPage(1);
        setCustomerPerPage(5);
        setCustomerTotalItems(0);
      }
    } finally {
      setCustomerLoading(false);
    }
  };

  const fetchBranchWiseOrder = async () => {
    setBranchLoading(true);
    try {
      const res = await apiRequestCall("post", backendRoute.branchWiseOrders, {
        data: { from: startDate, to: endDate, show_all: true },
      });
      const payload = res?.data?.data ?? {};
      if (res?.data?.status || res?.code === 200) {
        const nums = toNumberArray(payload?.data);
        const cats = toStringArray(payload?.xaxis);
        const n = Math.min(nums.length, cats.length);
        setBranchSeriesData(nums.slice(0, n));
        setBranchCategories(cats.slice(0, n));
        const y = payload?.yaxis || {};
        setBranchYAxis({
          min: Number(y.min ?? 0) || 0,
          max: Number(y.max ?? 0) || 0,
          tickAmount: Number(y.tickAmount ?? 5) || 5,
        });
      } else {
        setBranchSeriesData([]);
        setBranchCategories([]);
        setBranchYAxis({ min: 0, max: 0, tickAmount: 5 });
      }
    } finally {
      setBranchLoading(false);
    }
  };

  const fetchStatusWiseOrder = async () => {
    setStatusLoading(true);
    try {
      const res = await apiRequestCall("post", backendRoute.statusWiseOrders, {
        data: { from: startDate, to: endDate, show_all: true },
      });
      const series = res?.data?.data?.series;
      if (res?.data?.status || res?.code === 200) {
        const clean =
          Array.isArray(series) && series.length ? series : [0, 0, 0, 0];
        setStatusOrders(clean.map((n: any) => Number(n) || 0));
      } else {
        setStatusOrders([0, 0, 0, 0]);
      }
    } finally {
      setStatusLoading(false);
    }
  };

  const fetchPendingReasons = async () => {
    setReasonsLoading(true);
    try {
      const res = await apiRequestCall(
        "post",
        backendRoute.pendingDispatchReasons,
        {
          data: { from: startDate, to: endDate, show_all: true },
        }
      );
      const payload = res?.data?.data ?? {};
      const cats = Array.isArray(payload.categories) ? payload.categories : [];

      setReasonsCats(cats);
      setReasonsData(payload.series);
    } finally {
      setReasonsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderPrincipal({ page: 1, perPage });
    fetchCustomerOrder({ page: 1, perCustomerPage: 5 });
    fetchBranchWiseOrder();
    fetchStatusWiseOrder();
    fetchPendingReasons();
  }, [startDate, endDate]);

  useEffect(() => {
    setCurrentPage(1);
    fetchOrderPrincipal({ page: 1, perPage });
  }, [startDate, endDate, perPage]);

  useEffect(() => {
    fetchOrderPrincipal({ page: currentPage, perPage });
  }, [currentPage, perPage]);

  useEffect(() => {
    fetchCustomerOrder({ page: currentCustomerPage, perCustomerPage });
  }, [currentCustomerPage, perCustomerPage]);

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center ">
                <div className="col-md-4">
                  <h3 className="page-title">Order Summary</h3>
                </div>
                <div className="col-md-8 float-end ms-auto">
                  <div className="d-flex title-head">
                    <div className="daterange-picker d-flex align-items-center justify-content-center">
                      <div className="form-sort me-2">
                        <i className="ti ti-calendar" />
                        <DateRangePicker>
                          <input
                            className="form-control bookingrange"
                            type="text"
                          />
                        </DateRangePicker>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="row">
          <div className="col-md-6 d-flex" style={{ marginBottom: "-15px" }}>
            <div className="card w-100">
              <div className="card-header border-0 pb-0">
                <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-2">
                  <h6>
                    <i className="ti ti-grip-vertical me-1" /> Branch Wise
                    Orders
                  </h6>
                </div>
              </div>
              <div className="card-body">
                {branchLoading ? (
                  <Loader />
                ) : (
                  <Chart
                    options={branchOptions}
                    series={branchSeries}
                    type="area"
                    height={273}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-6 d-flex" style={{ marginBottom: "-15px" }}>
            <div className="card w-100">
              <div className="card-header">
                <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-2">
                  <h6>
                    <i className="ti ti-grip-vertical me-1" /> Status Wise
                    Orders
                  </h6>
                </div>
              </div>
              <div className="card-body">
                {statusLoading ? (
                  <Loader />
                ) : (
                  <Chart
                    options={pieOptions}
                    series={statusOrders}
                    type="pie"
                    width={400}
                    height={300}
                    style={{
                      TextAli: "red",
                    }}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="card">
              <div className="card-header border-0 pb-0">
                <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-2">
                  <h6>
                    <i className="ti ti-grip-vertical me-1" /> Pending Dispatch
                    Reasons
                  </h6>
                </div>
              </div>
              <div className="card-body">
                {reasonsLoading ? (
                  <Loader />
                ) : reasonsCats.length ? (
                  <Chart
                    options={reasonsOptions}
                    series={reasonsSeries}
                    type="bar"
                    height={Math.max(200, reasonsCats.length * 36)}
                  />
                ) : (
                  <div className="text-muted">No data</div>
                )}
              </div>
            </div>
          </div>
          <div className="col-md-4 d-flex">
            <div className="card w-100">
              <div className="card-header border-0 pb-0">
                <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-2">
                  <h6>
                    <i className="ti ti-grip-vertical me-1" /> Principal Wise
                    Orders
                  </h6>
                </div>
              </div>
              <div className="card-body">
                {principalLoading ? (
                  <Loader />
                ) : (
                  <Table data={principalOrder} columns={columns} />
                )}
                <Pagination
                  currentPage={currentPage}
                  perPage={perPage}
                  totalItems={totalItems}
                  onPageChange={setCurrentPage}
                  onPerPageChange={(n: number) => {
                    setPerPage(n);
                    setCurrentPage(1);
                  }}
                  perPageOptions={[
                    { value: 5, label: "5" },
                    { value: 10, label: "10" },
                    { value: 25, label: "25" },
                  ]}
                />
              </div>
            </div>
          </div>
          <div className="col-md-4 d-flex">
            <div className="card w-100">
              <div className="card-header border-0 pb-0">
                <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-2">
                  <h6>
                    <i className="ti ti-grip-vertical me-1" /> Customer Wise
                    Orders
                  </h6>
                </div>
              </div>
              <div className="card-body">
                {customerLoading ? (
                  <Loader />
                ) : (
                  <Table data={customerOrder} columns={columnCustomer} />
                )}
                <Pagination
                  currentPage={currentCustomerPage}
                  perPage={perCustomerPage}
                  totalItems={totalCustomerItems}
                  onPageChange={setCustomerCurrentPage}
                  onPerPageChange={(n: number) => {
                    setCustomerPerPage(n);
                    setCustomerCurrentPage(1);
                  }}
                  perPageOptions={[
                    { value: 5, label: "5" },
                    { value: 10, label: "10" },
                    { value: 25, label: "25" },
                  ]}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
